/**
 * @file ForgotPasswordPage.tsx
 * @brief `/forgot-password`: email → 6-digit code → new password → done,
 *        the phone's ForgotPasswordScreen on the web.
 *
 * @details
 * Same steps, titles, wording and API calls as the phone:
 * - Reset password: "No account with this email" as you type (the reset
 *   endpoint answers success either way, so a code would just never come).
 * - Verify code: six boxes split 3 + 3, paste fills them all, "Change
 *   Email", and "Re-send code" after a 60s cooldown per address. Coming
 *   back to the same address inside the cooldown sends nothing new.
 * - New password: the sign-up password step with the live rules.
 * - Password updated: then Sign in, with the email filled in.
 * Back walks back through the steps before it leaves the page, so a code
 * the user is about to type is never thrown away.
 *
 * @references
 * - FRONTEND/src/screens/Auth/ForgotPasswordScreen.tsx
 * - BACKEND/src/features/auth/routes.js (forgot-password/{init,verify,reset})
 *
 * @author DES
 * @date 26-05-2026
 * @updated 01-10-2026: the real flow replaces the "coming in Phase 2" stub
 */

import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Check, LoaderCircle, Mail } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { FormFieldGroup } from "@/components/ui/FormDialog";
import { Input } from "@/components/ui/Input";
import { bareCaptionClass, bareCaptionErrorClass } from "@/components/ui/fieldStyle";
import { ApiClientError, apiPost } from "@/shared/api/client";
import { authEndpoints } from "@/shared/api/endpoints";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import { checkEmailAvailable } from "./register/registerApi";
import { useTakenCheck, type RegisteredState } from "./register/registerShared";
import { PasswordStep, RegisterHeader, ServerError } from "./register/registerUi";

type Step = "EMAIL" | "OTP" | "PASSWORD" | "SUCCESS";

const STEP_TITLE: Record<Exclude<Step, "SUCCESS">, string> = {
  EMAIL: "Reset password",
  OTP: "Verify code",
  PASSWORD: "New password",
};

/** Minimum gap between two codes sent to the same address. */
const RESEND_COOLDOWN_MS = 60 * 1000;
const CODE_LENGTH = 6;
const NO_ACCOUNT = "No account with this email";

const emailSchema = z.object({
  email: z.string().trim().email("Enter a valid email"),
});
type EmailForm = z.infer<typeof emailSchema>;

const normalise = (e: string) => e.trim().toLowerCase();

/** "Taken" here means NO account: the reset needs one. */
const accountCheck = async (email: string) => {
  const free = await checkEmailAvailable(email);
  return free === null ? null : { available: !free };
};

function describeResetError(err: unknown): string {
  if (err instanceof ApiClientError) {
    switch (err.code) {
      case "RESET_EXPIRED":
        return "Code expired. Send a new one.";
      case "RESET_TOO_MANY_ATTEMPTS":
        return "Too many incorrect codes. Please request a new one.";
      case "INVALID_RESET":
        return "Invalid code";
      case "RATE_LIMITED":
        return "Too many password reset attempts. Please try again later.";
      default:
        if (err.status === 401) return "This code has expired. Start again.";
        return err.message || "Something went wrong. Please try again.";
    }
  }
  return "Something went wrong. Please try again.";
}

// ----------------------------------------------------------------------
// Six code boxes (FRONTEND/src/shared/components/OTPInput.tsx): 48×54,
// soft fill, bold 22, a dash after the third. One real input sits over
// them, so typing, backspace, paste and the browser's one-time-code
// autofill all just work.
// ----------------------------------------------------------------------
function CodeBoxes({
  value,
  onChange,
  error,
  autoFocus,
}: {
  value: string;
  onChange: (next: string) => void;
  error: boolean;
  autoFocus?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const active = Math.min(value.length, CODE_LENGTH - 1);
  return (
    <div className="relative">
      <div className="flex items-center justify-between gap-1.5" aria-hidden>
        {Array.from({ length: CODE_LENGTH }).map((_, i) => (
          <div key={i} className="contents">
            <div
              className={cn(
                "flex h-[54px] min-w-0 max-w-12 flex-1 items-center justify-center rounded-xl bg-secondary text-[22px] font-bold text-app-text transition-colors",
                focused && i === active && "bg-brand/10 ring-1 ring-brand/40",
                error && "ring-1 ring-destructive",
              )}
            >
              {value[i] ?? ""}
            </div>
            {i === 2 ? <span className="h-[1.5px] w-5 shrink-0 rounded-full bg-muted-foreground/40" /> : null}
          </div>
        ))}
      </div>
      <input
        aria-label="Verification code"
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus={autoFocus}
        maxLength={CODE_LENGTH}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH))}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className="absolute inset-0 h-full w-full cursor-text bg-transparent text-transparent caret-transparent outline-none selection:bg-transparent"
      />
    </div>
  );
}

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("EMAIL");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const [resetToken, setResetToken] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // ----------------------------------------------------------------------
  // Resend cooldown, per address
  // ----------------------------------------------------------------------
  const lastSent = useRef<{ email: string; at: number } | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const cooldownLeft = (addr: string) => {
    const last = lastSent.current;
    if (!last || last.email !== normalise(addr)) return 0;
    return Math.max(0, Math.ceil((last.at + RESEND_COOLDOWN_MS - now) / 1000));
  };
  const resendIn = cooldownLeft(email);
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [resendIn]);

  const sendCode = async (addr: string) => {
    await apiPost(authEndpoints.forgotInit, { email: addr });
    lastSent.current = { email: normalise(addr), at: Date.now() };
    setNow(Date.now());
  };

  // ----------------------------------------------------------------------
  // Step 1: email
  // ----------------------------------------------------------------------
  const emailForm = useForm<EmailForm>({
    resolver: zodResolver(emailSchema),
    mode: "onTouched",
    defaultValues: { email: "" },
  });
  const typed = (useWatch({ control: emailForm.control, name: "email" }) ?? "").trim();
  const noAccount = useTakenCheck(typed, z.string().email().safeParse(typed).success, accountCheck);
  const { setError, clearErrors, getFieldState } = emailForm;
  useEffect(() => {
    if (getFieldState("email").error?.type === "taken") clearErrors("email");
  }, [typed, getFieldState, clearErrors]);
  useEffect(() => {
    if (noAccount.takenValue) setError("email", { type: "taken", message: NO_ACCOUNT });
  }, [noAccount.takenValue, setError]);

  const onSendCode = emailForm.handleSubmit(async ({ email: raw }) => {
    const addr = raw.trim();
    setServerError(null);
    if (noAccount.takenValue || (await checkEmailAvailable(addr)) === true) {
      setError("email", { type: "taken", message: NO_ACCOUNT });
      return;
    }
    // Same address inside the cooldown: a code is already on its way.
    if (cooldownLeft(addr) > 0) {
      setEmail(addr);
      setStep("OTP");
      return;
    }
    setBusy(true);
    try {
      await sendCode(addr);
      setEmail(addr);
      setCode("");
      setCodeError(null);
      setStep("OTP");
    } catch (err) {
      setServerError(describeResetError(err));
    } finally {
      setBusy(false);
    }
  });

  // ----------------------------------------------------------------------
  // Step 2: code
  // ----------------------------------------------------------------------
  const onVerify = async (e?: FormEvent) => {
    e?.preventDefault();
    if (code.length !== CODE_LENGTH) {
      setCodeError("Enter the 6-digit code");
      return;
    }
    setBusy(true);
    setCodeError(null);
    try {
      const data = await apiPost<{ token?: string }>(authEndpoints.forgotVerify, {
        email,
        otp: code,
      });
      setResetToken(data?.token ?? "");
      setServerError(null);
      setStep("PASSWORD");
    } catch (err) {
      setCodeError(describeResetError(err));
    } finally {
      setBusy(false);
    }
  };

  const onResend = async () => {
    if (busy || cooldownLeft(email) > 0) return;
    setBusy(true);
    try {
      await sendCode(email);
      setCode("");
      setCodeError(null);
      toast({ title: "Success", description: "A new verification code has been sent." });
    } catch (err) {
      setCodeError(describeResetError(err));
    } finally {
      setBusy(false);
    }
  };

  // ----------------------------------------------------------------------
  // Back walks the steps, then leaves
  // ----------------------------------------------------------------------
  const onBack = () => {
    setServerError(null);
    if (step === "OTP") setStep("EMAIL");
    else if (step === "PASSWORD") setStep("OTP");
    else navigate("/login");
  };

  const toSignIn = () => {
    const state: RegisteredState = { registeredEmail: email, accountType: "personal" };
    navigate("/login", { replace: true, state });
  };

  const primaryBtn =
    "h-12 w-full bg-brand text-[15px] font-semibold text-brand-foreground hover:bg-brand/90";

  if (step === "SUCCESS") {
    return (
      <div className="flex flex-col items-center py-6 text-center">
        <Check
          className="h-28 w-28 text-emerald-500 duration-500 animate-in zoom-in-50 fade-in-0"
          strokeWidth={2.5}
          aria-hidden
        />
        <h2 className="mt-4 text-[28px] font-bold leading-[34px] tracking-tight text-foreground">
          Password updated
        </h2>
        <p className="mt-2 whitespace-pre-line text-[15px] leading-5 text-muted-foreground">
          {"Your password has been reset.\nSign in with the new one."}
        </p>
        <Button type="button" className={cn(primaryBtn, "mt-8")} size="lg" onClick={toSignIn}>
          Back to Sign in
        </Button>
      </div>
    );
  }

  if (step === "PASSWORD") {
    return (
      <PasswordStep
        title={STEP_TITLE.PASSWORD}
        subtitle="Create a secure password for your account."
        passwordLabel="New Password"
        submitLabel="Reset Password"
        busyLabel="Resetting…"
        onBack={onBack}
        serverError={serverError}
        onCreate={async (password) => {
          setServerError(null);
          try {
            await apiPost(authEndpoints.forgotReset, { email, token: resetToken, password });
            setStep("SUCCESS");
          } catch (err) {
            setServerError(describeResetError(err));
          }
        }}
      />
    );
  }

  if (step === "OTP") {
    return (
      <div>
        <RegisterHeader
          title={STEP_TITLE.OTP}
          subtitle={
            <>
              Enter the code sent to{" "}
              <span className="font-semibold text-app-text">{email}</span>
            </>
          }
          onBack={onBack}
        />
        <form onSubmit={onVerify} noValidate>
          {/* The caption is the error's place, as on every bare field. */}
          <span className={cn(bareCaptionClass, "mb-2", codeError && bareCaptionErrorClass)}>
            {codeError ?? "Verification code"}
          </span>
          <CodeBoxes
            value={code}
            onChange={(next) => {
              setCode(next);
              if (codeError) setCodeError(null);
            }}
            error={Boolean(codeError)}
            autoFocus
          />
          <div className="mt-4 flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={() => setStep("EMAIL")}
              className="font-semibold text-brand hover:underline"
            >
              Change Email
            </button>
            <button
              type="button"
              onClick={onResend}
              disabled={busy || resendIn > 0}
              className="font-medium text-app-text hover:underline disabled:cursor-default disabled:no-underline disabled:opacity-50"
            >
              {resendIn > 0 ? `Resend in ${resendIn}s` : "Re-send code"}
            </button>
          </div>
          <Button type="submit" className={cn(primaryBtn, "mt-8")} size="lg" disabled={busy}>
            {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : "Verify code"}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div>
      <RegisterHeader
        title={STEP_TITLE.EMAIL}
        subtitle="Enter your email address to receive a verification code."
        onBack={onBack}
      />
      <form onSubmit={onSendCode} className="space-y-3.5" noValidate>
        <FormFieldGroup
          label="Email Address"
          htmlFor="reset-email"
          icon={<Mail />}
          error={emailForm.formState.errors.email?.message}
        >
          <Input
            id="reset-email"
            type="email"
            autoComplete="email"
            autoFocus
            {...emailForm.register("email")}
          />
        </FormFieldGroup>
        <ServerError message={serverError} />
        <Button type="submit" className={cn(primaryBtn, "mt-4")} size="lg" disabled={busy}>
          {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : "Send code"}
        </Button>
      </form>
    </div>
  );
}
